import React from 'react'

import type { SweetspotMarkVariant } from '@/components/common/SweetspotMark'
import { SweetspotMark } from '@/components/common/SweetspotMark'

interface SharePointIconProps {
  size?: number
}

export const SharePointIcon: React.FC<SharePointIconProps> = ({
  size = 16,
}) => {
  const iconId = React.useId().replace(/:/g, '')
  const paint0Id = `${iconId}-paint0`
  const paint1Id = `${iconId}-paint1`
  const paint2Id = `${iconId}-paint2`
  const paint3Id = `${iconId}-paint3`
  const maskId = `${iconId}-mask0`

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="15.5" cy="11.5" r="9.5" fill={`url(#${paint0Id})`} />
      <circle cx="24" cy="17" r="8" fill={`url(#${paint1Id})`} />
      <mask
        id={maskId}
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="10"
        y="6"
        width="13"
        height="24"
      >
        <path
          d="M23 23.5C23 27.0899 20.0899 30 16.5 30C12.9101 30 10 27.0899 10 23.5C10 19.9102 10 6 10 6H23C23 6 23 21.1988 23 23.5Z"
          fill="#C4C4C4"
        />
      </mask>
      <g mask={`url(#${maskId})`}>
        <circle cx="16.5" cy="23.5" r="6.5" fill={`url(#${paint2Id})`} />
        <path
          d="M7 12C7 10.3431 8.34315 9 10 9H17C18.6569 9 20 10.3431 20 12V24C20 25.6569 18.6569 27 17 27H7V12Z"
          fill="#000000"
          fillOpacity="0.3"
        />
      </g>
      <rect y="7" width="18" height="18" rx="2" fill={`url(#${paint3Id})`} />
      <path
        d="M13 18.1229C13 16.5726 11.9602 15.8883 9.79665 15.0922C8.10273 14.4637 7.70021 14.2821 7.70021 13.6816C7.70021 13.1648 8.20335 12.8156 9.0587 12.8156C9.93082 12.8156 10.7526 13.1089 11.6751 13.6117L12.6143 11.9497C11.6247 11.3352 10.4507 11 9.02516 11C6.84486 11 5.28512 12.1173 5.28512 13.8212C5.28512 15.567 6.52621 16.1257 8.60587 16.8659C10.2662 17.4525 10.5849 17.7458 10.5849 18.2626C10.5849 18.8771 9.9979 19.1844 9.07547 19.1844C7.98532 19.1844 7.02935 18.8073 6.07338 18.1927L5 19.7849C6.174 20.595 7.63312 21 9.12579 21C11.3732 21 13 19.9385 13 18.1229Z"
        fill="white"
      />
      <defs>
        <linearGradient
          id={paint0Id}
          x1="6"
          y1="11.5"
          x2="26.5833"
          y2="11.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#103A3B" />
          <stop offset="1" stopColor="#116B6E" />
        </linearGradient>
        <linearGradient
          id={paint1Id}
          x1="18"
          y1="13"
          x2="32"
          y2="21"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#1D9097" />
          <stop offset="1" stopColor="#29BBC2" />
        </linearGradient>
        <linearGradient
          id={paint2Id}
          x1="12"
          y1="21.5"
          x2="23"
          y2="26.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#28A6B5" />
          <stop offset="1" stopColor="#31D6EC" />
        </linearGradient>
        <linearGradient
          id={paint3Id}
          x1="0"
          y1="16"
          x2="19.5"
          y2="16"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#105557" />
          <stop offset="1" stopColor="#116B6E" />
        </linearGradient>
      </defs>
    </svg>
  )
}

interface SweetspotIconProps {
  size?: number
  className?: string
  style?: React.CSSProperties
  /** `static-white` for the mark on dark chrome. */
  variant?: SweetspotMarkVariant
}

export const SweetspotIcon: React.FC<SweetspotIconProps> = ({
  size = 16,
  className,
  style,
  variant = 'static',
}) => (
  <SweetspotMark
    variant={variant}
    size={size}
    className={className}
    style={style}
  />
)

interface OutlookIconProps {
  size?: number
}

export const OutlookIcon: React.FC<OutlookIconProps> = ({ size = 16 }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 32 32"
      width={size}
      height={size}
    >
      <rect width={20} height={28} x={10} y={2} fill="#1066B5" rx={2} />
      <rect width={20} height={28} x={10} y={2} fill="url(#a)" rx={2} />
      <path fill="#32A9E7" d="M10 5h10v10H10z" />
      <path fill="#167EB4" d="M10 15h10v10H10z" />
      <path fill="#32A9E7" d="M20 15h10v10H20z" />
      <path fill="#58D9FD" d="M20 5h10v10H20z" />
      <mask
        id="c"
        width={24}
        height={16}
        x={8}
        y={14}
        maskUnits="userSpaceOnUse"
        style={{
          maskType: 'alpha',
        }}
      >
        <path
          fill="url(#b)"
          d="M8 14h22a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V14Z"
        />
      </mask>
      <g mask="url(#c)">
        <path fill="#135298" d="M32 14v4h-2v-4h2Z" />
        <path fill="url(#d)" d="M32 30V16L7 30h25Z" />
        <path fill="url(#e)" d="M8 30V16l25 14H8Z" />
      </g>
      <path
        fill="#000"
        fillOpacity={0.3}
        d="M8 12a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H8V12Z"
      />
      <rect width={18} height={18} y={7} fill="url(#f)" rx={2} />
      <path
        fill="#fff"
        d="M14 16.07v-.167C14 13.023 11.927 11 9.016 11 6.089 11 4 13.036 4 15.93v.167C4 18.977 6.073 21 9 21c2.911 0 5-2.036 5-4.93Zm-2.358.027c0 1.911-1.075 3.06-2.626 3.06-1.55 0-2.643-1.176-2.643-3.088v-.166c0-1.911 1.076-3.06 2.627-3.06 1.535 0 2.642 1.176 2.642 3.088v.166Z"
      />
      <defs>
        <linearGradient
          id="a"
          x1={10}
          x2={30}
          y1={16}
          y2={16}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#064484" />
          <stop offset={1} stopColor="#0F65B5" />
        </linearGradient>
        <linearGradient
          id="b"
          x1={8}
          x2={32}
          y1={26.769}
          y2={26.769}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#1B366F" />
          <stop offset={1} stopColor="#2657B0" />
        </linearGradient>
        <linearGradient
          id="d"
          x1={32}
          x2={8}
          y1={23}
          y2={23}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#44DCFD" />
          <stop offset={0.453} stopColor="#259ED0" />
        </linearGradient>
        <linearGradient
          id="e"
          x1={8}
          x2={32}
          y1={23}
          y2={23}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#259ED0" />
          <stop offset={1} stopColor="#44DCFD" />
        </linearGradient>
        <linearGradient
          id="f"
          x1={0}
          x2={18}
          y1={16}
          y2={16}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#064484" />
          <stop offset={1} stopColor="#0F65B5" />
        </linearGradient>
      </defs>
    </svg>
  )
}

interface GmailIconProps {
  size?: number
}

export const GmailIcon: React.FC<GmailIconProps> = ({ size = 16 }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="52 42 88 66"
      width={size}
      height={size}
    >
      <path fill="#4285f4" d="M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6" />
      <path fill="#34a853" d="M120 108h14c3.32 0 6-2.69 6-6V59l-20 15" />
      <path fill="#fbbc04" d="M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2" />
      <path fill="#ea4335" d="M72 74V48l24 18 24-18v26L96 92" />
      <path
        fill="#c5221f"
        d="M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2"
      />
    </svg>
  )
}

export default SweetspotIcon
